import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { Typography } from "../../core/Typography/Typography";
import { FC, HTMLAttributes, useEffect, useRef } from "react";
import { useStyles } from "./VideoCard.style";
import { Time } from "../../../models/time";
import { VideoItem } from "@onmo/graphql/graphql";
import {
  loadYouTubeApi,
  YouTubePlayer,
} from "../../../models/youtubeIframeApi";
import { useVideoContentView } from "../../../hooks/useVideoContentView";

export const VideoCard: FC<
  {
    video: VideoItem;
    isPlayingVideo?: boolean;
    onContainerRef?: (element: HTMLDivElement | null) => void;
  } & HTMLAttributes<HTMLDivElement>
> = ({ video, isPlayingVideo, onContainerRef, ...otherProps }) => {
  const classes = useStyles();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const playerRef = useRef<YouTubePlayer | null>(null);
  const onVideoEnded = useVideoContentView(isPlayingVideo);
  const onEndedRef = useRef(onVideoEnded);

  useEffect(() => {
    onEndedRef.current = onVideoEnded;
  }, [onVideoEnded]);

  useEffect(() => {
    if (!isPlayingVideo) return;
    let cancelled = false;

    loadYouTubeApi()
      .then((YT) => {
        if (cancelled || !iframeRef.current) return;
        playerRef.current = new YT.Player(iframeRef.current, {
          events: {
            onStateChange: (event) => {
              if (event.data === YT.PlayerState.ENDED) onEndedRef.current();
            },
          },
        });
      })
      .catch(() => {
        //do nothing
      });

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy();
      } catch {
        //do nothing
      }
      playerRef.current = null;
    };
  }, [isPlayingVideo, video.id]);

  const renderVideo = () => {
    if (isPlayingVideo) {
      const params = new URLSearchParams({
        autoplay: "1",
        mute: "1",
        enablejsapi: "1",
        origin: window.location.origin,
      });
      return (
        <iframe
          ref={iframeRef}
          className={classes.videoThumbnail}
          src={`https://www.youtube.com/embed/${video.id}?${params}`}
          title={"video" + video.id}
          allowFullScreen
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        />
      );
    } else {
      return (
        <img
          src={video.thumbnailUrl.high ?? ""}
          alt="Video thumbnail"
          className={classes.videoThumbnail}
        />
      );
    }
  };

  return (
    <div {...otherProps} ref={onContainerRef}>
      {renderVideo()}
      <div className={classes.videoInfo}>
        <div className={classes.info}>
          <Typography variant={TypographyVariant.BodyB4Medium} lines={1}>
            {video.title}
          </Typography>
          <div className={classes.description}>
            <Typography
              variant={TypographyVariant.BodyB5Regular}
              color={TypographyColor.Silver}
              lines={1}
              className={classes.descriptionContent}
            >
              {video.description}
            </Typography>
            <Typography
              variant={TypographyVariant.BodyB6Regular}
              color={TypographyColor.Silver}
              lines={1}
            >
              {Time.getTimeAgo(video.publishedAt)}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoCard;
